/**
 * Cliente do portão de login único (worker, apps/worker/src/gate.ts) para os painéis de infraestrutura
 * (status.vixio.com.br, analytics.vixio.com.br…). O painel troca o access token do Supabase pelo cookie vx_gate,
 * gravado pelo worker em Domain=.vixio.com.br, e devolve a pessoa ao endereço de origem.
 */
import { env } from "../env";

export type GateError =
  | "forbidden"
  | "mfa_required"
  | "invalid_next"
  | "invalid_token"
  | "too_many_requests"
  | "gate_not_configured"
  | "unavailable";

export type GateResult = { ok: true; redirect: string | null } | { ok: false; error: GateError };

/** Mensagens em pt-BR para cada recusa do portão. */
export const GATE_MESSAGES: Record<GateError, { title: string; text: string }> = {
  forbidden: {
    title: "Sem permissão",
    text: "Os painéis de infraestrutura (status dos serviços e estatísticas do site) são só para administradores. Se precisa de acesso, peça a um administrador da sua organização.",
  },
  mfa_required: {
    title: "Confirme a verificação em duas etapas",
    text: "Sua conta usa autenticador. Digite o código para continuar.",
  },
  invalid_next: {
    title: "Endereço de destino inválido",
    text: "O link que trouxe você até aqui não aponta para um painel da Vixio. Abra o painel desejado de novo pelo menu Configurações → Painéis de infraestrutura.",
  },
  invalid_token: {
    title: "Sessão expirada",
    text: "Sua sessão no painel expirou. Saia e entre de novo.",
  },
  too_many_requests: {
    title: "Muitas tentativas",
    text: "Aguarde um minuto e tente de novo.",
  },
  gate_not_configured: {
    title: "Portão ainda não configurado",
    text: "O portão de acesso aos painéis não está ligado neste servidor. Avise a equipe técnica.",
  },
  unavailable: {
    title: "Não foi possível liberar o acesso",
    text: "O portão de acesso não respondeu. Tente de novo em instantes; se continuar, avise a equipe técnica.",
  },
};

export function gateUrl(): string {
  return env.gateUrl.replace(/\/+$/, "");
}

/** Só segue para https (o worker confere de novo a lista de hosts: nada de open redirect). */
export function isHttpsUrl(v: string | null | undefined): v is string {
  if (!v) return false;
  try {
    return new URL(v).protocol === "https:";
  } catch {
    return false;
  }
}

export async function openGateSession(accessToken: string, next: string | null): Promise<GateResult> {
  let res: Response;
  try {
    res = await fetch(`${gateUrl()}/gate/session`, {
      method: "POST",
      credentials: "include",
      headers: { authorization: `Bearer ${accessToken}`, "content-type": "application/json" },
      body: JSON.stringify(next ? { next } : {}),
    });
  } catch {
    return { ok: false, error: "unavailable" };
  }
  let body: { ok?: boolean; redirect?: string | null; error?: string } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    /* corpo vazio */
  }
  if (res.ok && body.ok) return { ok: true, redirect: isHttpsUrl(body.redirect) ? body.redirect : null };
  const e = body.error as GateError | undefined;
  if (res.status === 429) return { ok: false, error: "too_many_requests" };
  if (e && e in GATE_MESSAGES) return { ok: false, error: e };
  return { ok: false, error: res.status === 401 ? "invalid_token" : "unavailable" };
}

/** Encerra o acesso aos painéis (apaga o cookie do portão). Nunca lança. */
export async function closeGateSession(): Promise<boolean> {
  if (!env.gateUrl) return false;
  try {
    const res = await fetch(`${gateUrl()}/gate/logout`, { method: "POST", credentials: "include", keepalive: true });
    return res.ok;
  } catch {
    return false;
  }
}
